import {
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
} from '@chakra-ui/react';

// eslint-disable-next-line import/no-cycle
import { CPrimaryButton, CSecondaryButton } from '../Buttons';
import { responsiveStyles } from '../Chakra.utils';
import { ChakraSizes, Spacing } from '../ChakraTheme';
import { CRow } from '../Layouts';
import { CSubHeader } from '../Typography';
import { useDrawer } from './Drawer.hooks';
import { DrawerProps } from './Drawer.types';

export const CDrawer = ({
    header,
    children,
    testId,
    disableApply = false,
    disableCancel = false,
    cancelButtonText = 'Cancel',
    isEmbedded = false,
    size = ChakraSizes.lg,
    hasFooter = true,
    headerSlot,
    footerSlot,
    ...props
}: DrawerProps): JSX.Element => {
    const { handleApply, onClose } = useDrawer({ ...props });

    return (
        <Drawer
            data-test={`${testId}-drawer`}
            size={size}
            placement="right"
            {...props}
        >
            <DrawerOverlay />
            <DrawerContent>
                {headerSlot ? (
                    <DrawerHeader
                        gap={Spacing.SM}
                        display="flex"
                        justifyContent="end"
                        w="auto"
                        position="absolute"
                        top={0}
                        right={0}
                        alignItems="center"
                        py={Spacing.SM}
                        px={Spacing.MD}
                    >
                        {headerSlot}
                        {!disableCancel && (
                            <DrawerCloseButton
                                position="relative"
                                top={0}
                                right={0}
                                data-test={`${testId}-drawer--close`}
                            />
                        )}
                    </DrawerHeader>
                ) : (
                    <>
                        {!disableCancel && (
                            <DrawerCloseButton
                                data-test={`${testId}-drawer--close`}
                            />
                        )}
                    </>
                )}
                {header && (
                    <DrawerHeader>
                        <CSubHeader text={header} />
                    </DrawerHeader>
                )}
                <DrawerBody flex={isEmbedded ? 'unset' : undefined}>
                    {children}
                </DrawerBody>
                {hasFooter && (
                    <DrawerFooter>
                        <CRow
                            alignItems={responsiveStyles('flex-end', 'center')}
                            justifyContent={responsiveStyles(
                                'flex-end',
                                'space-between'
                            )}
                        >
                            {footerSlot}
                            <CRow justifyContent="end" noBreak>
                                {handleApply ? (
                                    <CSecondaryButton
                                        testId={`${testId}-drawer--cancel`}
                                        onClick={onClose}
                                    >
                                        {cancelButtonText}
                                    </CSecondaryButton>
                                ) : (
                                    <CPrimaryButton
                                        testId={`${testId}-drawer--cancel`}
                                        isDisabled={disableCancel}
                                        onClick={onClose}
                                    >
                                        {cancelButtonText}
                                    </CPrimaryButton>
                                )}
                                {handleApply && (
                                    <CPrimaryButton
                                        isDisabled={disableApply}
                                        testId={`${testId}-drawer--apply`}
                                        onClick={handleApply}
                                    >
                                        Apply
                                    </CPrimaryButton>
                                )}
                            </CRow>
                        </CRow>
                    </DrawerFooter>
                )}
            </DrawerContent>
        </Drawer>
    );
};
